/** UI strings for the F04 models (Compton, x-ray tube, pair); merged into the photons dictionary. */
export const sv = {
  comptonTitle: "Comptonspridning: rörelsemängdstriangel och energibudget",
  comptonNote:
    "Fri partikel i vila före stöten. Fotonen kommer längs +x och sprids vinkeln φ; partikeln rekylerar på andra sidan axeln med vinkeln θ_e. Comptonskiftet Δλ beror bara på φ och partikelns massa, inte på λ.",
  comptonWavelength: "Infallande λ (pm)",
  comptonAngle: "Spridningsvinkel φ (°)",
  comptonTarget: "Spridande partikel",
  comptonTargetElectron: "Fri elektron",
  comptonTargetProton: "Proton",
  comptonTargetAtom: "Hel kolatom",
  comptonEqualAngles: "Välj λ så att θ_e = φ",
  comptonBackscatterButton: "Bakåtspridning φ = 180°",
  comptonTriangleSvg: "Rörelsemängdstriangel",
  comptonTriangleDesc:
    "Infallande fotons rörelsemängd p längs axeln, spridd fotons rörelsemängd p′ i vinkeln φ och partikelns rörelsemängd P_e som sluter triangeln p = p′ + P_e. Alla pilar i samma skala.",
  comptonShiftSvg: "Comptonskift mot spridningsvinkel",
  comptonShiftDesc:
    "Kurvan visar Δλ/λ_C = 1 − cos φ från 0 till 180 grader. Punkten markerar aktuell vinkel. Kurvan ändras inte när λ ändras.",
  comptonAxisAngle: "φ (°)",
  comptonAxisShift: "Δλ/λ_C",
  comptonBarsSvg: "Energibudget",
  comptonBarsDesc:
    "Tre staplar relativt den infallande fotonens energi: E, den spridda fotonens energi E′ och partikelns kinetiska energi K_e = E − E′.",
  barIncident: "E",
  barScattered: "E′",
  barKinetic: "K_e",
  comptonEnergy: "E = hc/λ (keV)",
  comptonLambdaC: "λ_C = h/(Mc) (pm)",
  comptonShift: "Δλ = λ_C(1 − cos φ) (pm)",
  comptonScatteredWavelength: "λ′ (pm)",
  comptonRelativeShift: "Δλ/λ",
  comptonScatteredEnergy: "E′ = hc/λ′ (keV)",
  comptonFraction: "E′/E",
  comptonKinetic: "K_e = E − E′ (keV)",
  comptonRecoilMomentum: "P_e (kg m/s)",
  comptonRecoilAngle: "θ_e (°)",
  comptonRest: "Viloenergi Mc² (keV)",
  comptonMassShell: "Masskal: √(E_e² − (P_e c)²) (keV)",
  comptonBackscatter: "E′ vid φ = 180° (keV)",
  comptonEdge: "Comptonkant: största K_e (keV)",
  comptonFormula: String.raw`Comptonformeln (38.7): $\lambda'-\lambda=\dfrac{h}{Mc}(1-\cos\varphi)$. Rekylvinkel: $\tan\theta_e=\dfrac{\cot(\varphi/2)}{1+E/(Mc^2)}$. Bakåtspridning: $E'=\dfrac{E}{1+2E/(Mc^2)}<\dfrac{Mc^2}{2}$.`,

  xrayTitle: "Röntgenrör: bromsstrålning, kortvågsgräns och karakteristiska linjer",
  xrayNote:
    "Kortvågsgränsen, linjeenergierna och K-kanten är kvantitativa. Kontinuumets form (Kramers) och linjernas höjder är schematiska; ingen filtrering och ingen självabsorption. Kurvans höjd är proportionell mot rörströmmen. Formen och kortvågsgränsen är materialoberoende; höjden växer med Z (schematiskt).",
  xrayViewWavelength: "Per våglängd",
  xrayViewEnergy: "Per energi",
  xrayViewBragg: "Braggspektrometer",
  xrayAxisWavelength: "λ (pm)",
  xrayAxisEnergy: "E (keV)",
  xrayAxisTwoTheta: "2θ (°)",
  xrayAxisIntensity: "intensitet (godt. enh.)",
  xrayVoltage: "Accelerationsspänning V (kV)",
  xrayCurrent: "Rörström i (mA)",
  xrayAnode: "Anod",
  xraySpacing: "Planavstånd d (nm)",
  xrayDoubleCurrent: "Fördubbla strömmen",
  xraySpectrumSvg: "Röntgenspektrum",
  xraySpectrumDesc:
    "Röntgenrörets spektrum: kontinuerlig bromsstrålning som slutar vid kortvågsgränsen och, över K-kanten, smala Kα- och Kβ-linjer. Streckade lodräta linjer markerar kortvågsgränsen och Kα. En streckad kurva visar jämförelsen.",
  xrayBraggSvg: "Braggspektrometer: intensitet mot 2θ",
  xrayBraggDesc:
    "Signal i detektorn mot spridningsvinkeln 2θ för en kristall med planavståndet d. Varje ordning n reflekterar λ = 2d sin θ/n, så ordningarna överlappar. Streckade linjer markerar första ordningens kortvågsgräns och Kα i varje ordning.",
  xrayTubeEnergy: "eV (keV)",
  xrayCutoff: "Kortvågsgräns λ_min = hc/(eV) (pm)",
  xrayContinuumPeak: "Kontinuumtopp ≈ 1,5 λ_min (pm)",
  xrayKAlpha: "Kα (keV)",
  xrayKAlphaWavelength: "λ för Kα (pm)",
  xrayKBeta: "Kβ (keV)",
  xrayKEdge: "K-kant (keV)",
  xrayOvervoltage: "U = eV/E_K",
  xrayLines: "K-linjer",
  xrayLinesOn: "exciterade",
  xrayLinesOff: "ej exciterade (eV < K-kant)",
  xrayMoseley: "Moseley (Z − 1)² · 10,2 eV (keV)",
  xrayReferenceCutoff: "Jämförelse: λ_min (pm)",
  xrayBraggOrders: "2θ för Kα, n = 1, 2, … (°)",
  xrayBraggNone: "ingen",
  xrayBraggCutoff: "Första ordningens kortvågsgräns 2θ_min (°)",
  xrayFormula: String.raw`Kortvågsgräns (38.6): $eV=hf_\mathrm{max}=hc/\lambda_\mathrm{min}$, oberoende av anod och ström. Moseley (41.48): $E_{K\alpha}\approx(Z-1)^2\cdot10{,}2\,\mathrm{eV}$. Braggs lag: $2d\sin\theta=n\lambda$; detektorn står vid $2\theta$.`,

  pairTitle: "Parbildning och annihilation",
  pairNote:
    "Parbildning: e⁺ och e⁻ med samma kinetiska energi i vinklarna ±α mot fotonen; partnern rekylerar framåt (Challenge 38.39). Annihilation: e⁺ längs +x möter e⁻ längs −x; två fotoner.",
  pairMode: "Process",
  pairModeProduction: "Parbildning",
  pairModeAnnihilation: "Annihilation",
  pairTarget: "Partner som tar upp rörelsemängd",
  pairTargetNone: "Ingen (vakuum)",
  pairTargetElectron: "Elektron",
  pairTargetProton: "Proton",
  pairTargetLead: "Blykärna",
  pairKinetic: "K för e⁺ och e⁻ (MeV)",
  pairAlpha: "Vinkel α mot fotonen (°)",
  pairImpossible: "omöjlig",
  pairImpossibleVacuum:
    "Utan partner kräver bevarad energi och rörelsemängd att β cos α = 1. Ingen massiv partikel når dit: fotonen ensam kan inte bilda ett par.",
  pairImpossibleTarget:
    "Ingen rekylfart löser både energi- och rörelsemängdslagen för denna partner: paret är för långsamt eller för brett.",
  pairPlaneSvg: "Parbildning i (pc, E)-planet",
  pairPlaneDesc:
    "Fotonen ligger på ljuslinjen E = pc. Paret ligger ovanför den streckade hyperbeln E = √((pc)² + (2mc²)²). Partnerns rekyl sluter figuren: den har rörelsemängd men nästan ingen energi.",
  pairAxisMomentum: "pc (MeV)",
  pairAxisEnergy: "E (MeV)",
  pairLegendPhoton: "foton",
  pairLegendPair: "e⁺e⁻-par",
  pairLegendRecoil: "partnerns rekyl",
  pairLegendShell: "streckat: E = pc och 2mc²",
  pairPositronKinetic: "K₊ för positronen (MeV)",
  pairElectronKinetic: "K₋ för elektronen (MeV)",
  pairPhotonAngle: "Foton 1:s vinkel ψ (°)",
  pairSymmetric: "Symmetrisk emission",
  pairMomentaSvg: "Rörelsemängder vid annihilation",
  pairMomentaDesc:
    "Positronens och elektronens rörelsemängder längs axeln ger totalen P. Foton 1 lämnar i vinkeln ψ och foton 2 sluter triangeln p₁ + p₂ = P.",
  pairLegendPositron: "e⁺",
  pairLegendElectron: "e⁻",
  pairLegendFirst: "foton 1",
  pairLegendSecond: "foton 2",
  pairLeptonGamma: "γ för varje lepton",
  pairLeptonSpeed: "v/c för varje lepton",
  pairEnergy: "Parets energi (MeV)",
  pairMomentum: "Parets rörelsemängd · c (MeV)",
  pairPhoton: "Fotonens energi E_γ (MeV)",
  pairPhotonWavelength: "Fotonens λ (pm)",
  pairThreshold: "Tröskel 2mc²(1 + m/M) (MeV)",
  pairAboveThreshold: "E_γ − tröskel (eV)",
  pairRecoilSpeed: "Partnerns fart V (km/s)",
  pairRecoilKinetic: "Partnerns kinetiska energi (eV)",
  pairRecoilMomentum: "Partnerns rörelsemängd · c (MeV)",
  pairTotalEnergy: "Total energi (MeV)",
  pairTotalMomentum: "Total rörelsemängd · c (MeV)",
  pairInvariantMass: "Invariant massa √s (MeV)",
  pairCentreSpeed: "Masscentrumsystemets fart (c)",
  pairFirstEnergy: "E₁ (MeV)",
  pairSecondEnergy: "E₂ (MeV)",
  pairFirstWavelength: "λ₁ (pm)",
  pairSecondWavelength: "λ₂ (pm)",
  pairSecondAngle: "Foton 2:s vinkel (°)",
  pairOpening: "Vinkel mellan fotonerna (°)",
  pairFormula: String.raw`Tröskel med partnern i vila: $E_\gamma\ge2mc^2(1+m/M)$, från invarianten $(E_\gamma+Mc^2)^2-E_\gamma^2\ge(2mc^2+Mc^2)^2$. Annihilation: $E_1=\dfrac{s}{2(E-Pc\cos\psi)}$ med $s=E^2-(Pc)^2$.`,
};

export const en: Record<keyof typeof sv, string> = {
  comptonTitle: "Compton scattering: momentum triangle and energy budget",
  comptonNote:
    "Free particle at rest before the collision. The photon arrives along +x and scatters by φ; the particle recoils on the other side of the axis at θ_e. The Compton shift Δλ depends only on φ and the particle's mass, not on λ.",
  comptonWavelength: "Incident λ (pm)",
  comptonAngle: "Scattering angle φ (°)",
  comptonTarget: "Scattering particle",
  comptonTargetElectron: "Free electron",
  comptonTargetProton: "Proton",
  comptonTargetAtom: "Whole carbon atom",
  comptonEqualAngles: "Choose λ so that θ_e = φ",
  comptonBackscatterButton: "Backscatter φ = 180°",
  comptonTriangleSvg: "Momentum triangle",
  comptonTriangleDesc:
    "Incident photon momentum p along the axis, scattered photon momentum p′ at angle φ and particle momentum P_e closing the triangle p = p′ + P_e. All arrows at the same scale.",
  comptonShiftSvg: "Compton shift against scattering angle",
  comptonShiftDesc:
    "The curve shows Δλ/λ_C = 1 − cos φ from 0 to 180 degrees. The dot marks the current angle. The curve does not change when λ changes.",
  comptonAxisAngle: "φ (°)",
  comptonAxisShift: "Δλ/λ_C",
  comptonBarsSvg: "Energy budget",
  comptonBarsDesc:
    "Three bars relative to the incident photon energy: E, the scattered photon energy E′ and the particle's kinetic energy K_e = E − E′.",
  barIncident: "E",
  barScattered: "E′",
  barKinetic: "K_e",
  comptonEnergy: "E = hc/λ (keV)",
  comptonLambdaC: "λ_C = h/(Mc) (pm)",
  comptonShift: "Δλ = λ_C(1 − cos φ) (pm)",
  comptonScatteredWavelength: "λ′ (pm)",
  comptonRelativeShift: "Δλ/λ",
  comptonScatteredEnergy: "E′ = hc/λ′ (keV)",
  comptonFraction: "E′/E",
  comptonKinetic: "K_e = E − E′ (keV)",
  comptonRecoilMomentum: "P_e (kg m/s)",
  comptonRecoilAngle: "θ_e (°)",
  comptonRest: "Rest energy Mc² (keV)",
  comptonMassShell: "Mass shell: √(E_e² − (P_e c)²) (keV)",
  comptonBackscatter: "E′ at φ = 180° (keV)",
  comptonEdge: "Compton edge: largest K_e (keV)",
  comptonFormula: String.raw`Compton formula (38.7): $\lambda'-\lambda=\dfrac{h}{Mc}(1-\cos\varphi)$. Recoil angle: $\tan\theta_e=\dfrac{\cot(\varphi/2)}{1+E/(Mc^2)}$. Backscatter: $E'=\dfrac{E}{1+2E/(Mc^2)}<\dfrac{Mc^2}{2}$.`,

  xrayTitle: "X-ray tube: bremsstrahlung, cutoff wavelength and characteristic lines",
  xrayNote:
    "The cutoff wavelength, line energies and K edge are quantitative. The continuum shape (Kramers) and line heights are schematic; no filtering and no self-absorption. The curve height is proportional to the tube current. The shape and cutoff are independent of the anode; the height grows with Z (schematic).",
  xrayViewWavelength: "Per wavelength",
  xrayViewEnergy: "Per energy",
  xrayViewBragg: "Bragg spectrometer",
  xrayAxisWavelength: "λ (pm)",
  xrayAxisEnergy: "E (keV)",
  xrayAxisTwoTheta: "2θ (°)",
  xrayAxisIntensity: "intensity (arb. units)",
  xrayVoltage: "Accelerating voltage V (kV)",
  xrayCurrent: "Tube current i (mA)",
  xrayAnode: "Anode",
  xraySpacing: "Plane spacing d (nm)",
  xrayDoubleCurrent: "Double the current",
  xraySpectrumSvg: "X-ray spectrum",
  xraySpectrumDesc:
    "The tube spectrum: continuous bremsstrahlung ending at the cutoff wavelength and, above the K edge, narrow Kα and Kβ lines. Dashed vertical lines mark the cutoff and Kα. A dashed curve shows the comparison.",
  xrayBraggSvg: "Bragg spectrometer: intensity against 2θ",
  xrayBraggDesc:
    "Detector signal against the scattering angle 2θ for a crystal with plane spacing d. Each order n reflects λ = 2d sin θ/n, so the orders overlap. Dashed lines mark the first-order cutoff and Kα in every order.",
  xrayTubeEnergy: "eV (keV)",
  xrayCutoff: "Cutoff λ_min = hc/(eV) (pm)",
  xrayContinuumPeak: "Continuum peak ≈ 1.5 λ_min (pm)",
  xrayKAlpha: "Kα (keV)",
  xrayKAlphaWavelength: "λ of Kα (pm)",
  xrayKBeta: "Kβ (keV)",
  xrayKEdge: "K edge (keV)",
  xrayOvervoltage: "U = eV/E_K",
  xrayLines: "K lines",
  xrayLinesOn: "excited",
  xrayLinesOff: "not excited (eV < K edge)",
  xrayMoseley: "Moseley (Z − 1)² · 10.2 eV (keV)",
  xrayReferenceCutoff: "Comparison: λ_min (pm)",
  xrayBraggOrders: "2θ for Kα, n = 1, 2, … (°)",
  xrayBraggNone: "none",
  xrayBraggCutoff: "First-order cutoff 2θ_min (°)",
  xrayFormula: String.raw`Cutoff (38.6): $eV=hf_\mathrm{max}=hc/\lambda_\mathrm{min}$, independent of anode and current. Moseley (41.48): $E_{K\alpha}\approx(Z-1)^2\cdot10.2\,\mathrm{eV}$. Bragg's law: $2d\sin\theta=n\lambda$; the detector sits at $2\theta$.`,

  pairTitle: "Pair production and annihilation",
  pairNote:
    "Pair production: e⁺ and e⁻ with equal kinetic energy at ±α to the photon; the partner recoils forward (Challenge 38.39). Annihilation: e⁺ along +x meets e⁻ along −x; two photons.",
  pairMode: "Process",
  pairModeProduction: "Pair production",
  pairModeAnnihilation: "Annihilation",
  pairTarget: "Partner taking up momentum",
  pairTargetNone: "None (vacuum)",
  pairTargetElectron: "Electron",
  pairTargetProton: "Proton",
  pairTargetLead: "Lead nucleus",
  pairKinetic: "K of e⁺ and e⁻ (MeV)",
  pairAlpha: "Angle α to the photon (°)",
  pairImpossible: "impossible",
  pairImpossibleVacuum:
    "Without a partner, conserving energy and momentum requires β cos α = 1. No massive particle gets there: a photon alone cannot make a pair.",
  pairImpossibleTarget:
    "No recoil speed satisfies both energy and momentum conservation for this partner: the pair is too slow or too wide.",
  pairPlaneSvg: "Pair production in the (pc, E) plane",
  pairPlaneDesc:
    "The photon lies on the light line E = pc. The pair lies above the dashed hyperbola E = √((pc)² + (2mc²)²). The partner's recoil closes the figure: it has momentum but almost no energy.",
  pairAxisMomentum: "pc (MeV)",
  pairAxisEnergy: "E (MeV)",
  pairLegendPhoton: "photon",
  pairLegendPair: "e⁺e⁻ pair",
  pairLegendRecoil: "partner recoil",
  pairLegendShell: "dashed: E = pc and 2mc²",
  pairPositronKinetic: "K₊ of the positron (MeV)",
  pairElectronKinetic: "K₋ of the electron (MeV)",
  pairPhotonAngle: "Angle ψ of photon 1 (°)",
  pairSymmetric: "Symmetric emission",
  pairMomentaSvg: "Momenta in annihilation",
  pairMomentaDesc:
    "The positron and electron momenta along the axis give the total P. Photon 1 leaves at ψ and photon 2 closes the triangle p₁ + p₂ = P.",
  pairLegendPositron: "e⁺",
  pairLegendElectron: "e⁻",
  pairLegendFirst: "photon 1",
  pairLegendSecond: "photon 2",
  pairLeptonGamma: "γ of each lepton",
  pairLeptonSpeed: "v/c of each lepton",
  pairEnergy: "Pair energy (MeV)",
  pairMomentum: "Pair momentum · c (MeV)",
  pairPhoton: "Photon energy E_γ (MeV)",
  pairPhotonWavelength: "Photon λ (pm)",
  pairThreshold: "Threshold 2mc²(1 + m/M) (MeV)",
  pairAboveThreshold: "E_γ − threshold (eV)",
  pairRecoilSpeed: "Partner speed V (km/s)",
  pairRecoilKinetic: "Partner kinetic energy (eV)",
  pairRecoilMomentum: "Partner momentum · c (MeV)",
  pairTotalEnergy: "Total energy (MeV)",
  pairTotalMomentum: "Total momentum · c (MeV)",
  pairInvariantMass: "Invariant mass √s (MeV)",
  pairCentreSpeed: "Centre-of-momentum speed (c)",
  pairFirstEnergy: "E₁ (MeV)",
  pairSecondEnergy: "E₂ (MeV)",
  pairFirstWavelength: "λ₁ (pm)",
  pairSecondWavelength: "λ₂ (pm)",
  pairSecondAngle: "Angle of photon 2 (°)",
  pairOpening: "Angle between the photons (°)",
  pairFormula: String.raw`Threshold with the partner at rest: $E_\gamma\ge2mc^2(1+m/M)$, from the invariant $(E_\gamma+Mc^2)^2-E_\gamma^2\ge(2mc^2+Mc^2)^2$. Annihilation: $E_1=\dfrac{s}{2(E-Pc\cos\psi)}$ with $s=E^2-(Pc)^2$.`,
};
